import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios, { readProfileUser } from '../utils/api';
import { portalLoginUrl } from '../utils/portalLogin';
import GenerationPreviewButton from '../components/GenerationPreviewButton';
import { formatBalanceUSD, formatGenerationTemplate, formatGenerationSiteType } from '../utils/adminHelpers';

interface User {
  id: number;
  email: string;
  role: string;
  balance: number;
  currency?: 'USD';
  generation_limit: number;
  created_at: string;
}

interface Generation {
  id: number;
  generation_id: string;
  template_type: string;
  jurisdiction: string;
  site_type: string;
  language: string;
  status: string;
  created_at: string;
  archive_path?: string;
}

interface AdminGeneration extends Generation {
  user_email: string;
}

type ServiceSummary = {
  totalServices: number;
  availableStock: number;
  availableFallback: number;
  unchecked: number;
  lastFullCheck: string | null;
};

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString('ru-RU', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getStatusText(status: string) {
  switch (status) {
    case 'completed':
      return 'Завершено';
    case 'pending':
      return 'В обработке';
    case 'failed':
      return 'Ошибка';
    case 'processing':
      return 'Обработка';
    default:
      return status;
  }
}

function getStatusPillClass(status: string) {
  if (status === 'completed') return 'is-ok';
  if (status === 'failed') return 'is-bad';
  return 'is-warn';
}

const CreateSiteActions: React.FC = () => (
  <section className="u2-card wp-createSiteActions" aria-label="Создать сайт">
    <div className="u2-cardInner">
      <h2 className="u2-title">Создать сайт</h2>
      <p className="u2-subtitle">Выберите удобный способ. Оба режима создают готовый сайт с текстами, изображениями и документами.</p>
      <div className="wp-createModes">
        <Link to="/generator-v2?mode=quick" className="wp-createMode">
          <strong>Быстро и просто</strong>
          <span>Выберите шаблон, тематику, язык, регион и фон. Остальное подберём автоматически.</span>
          <span className="u2-btn is-success">Сделать быстро и просто →</span>
        </Link>
        <Link to="/generator-v2?mode=full" className="wp-createMode">
          <strong>Все настройки</strong>
          <span>Настройте оформление, состав блоков и данные компании.</span>
          <span className="u2-btn is-success">Создать со всеми настройками →</span>
        </Link>
      </div>
    </div>
  </section>
);

const DashboardV5: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [generations, setGenerations] = useState<Generation[]>([]);
  const [adminGenerations, setAdminGenerations] = useState<AdminGeneration[]>([]);
  const [servicesSummary, setServicesSummary] = useState<ServiceSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pricing, setPricing] = useState<{ single_page: number; multi_page: number; free?: { remaining: number } | null } | null>(null);

  const isAdmin = user?.role === 'admin';

  const completedCount = useMemo(() => generations.filter((g) => g.status === 'completed').length, [generations]);
  const pendingCount = useMemo(() => generations.filter((g) => ['pending', 'processing', 'queued'].includes(g.status)).length, [generations]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');
      if (!token) {
        setError('Необходимо войти в систему');
        return;
      }

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const userResponse = await axios.get('/api/auth/profile');
      const userData = readProfileUser(userResponse.data) as User | null;
      setUser(userData);

      if (userData?.role === 'admin') {
        const [generationsResponse, servicesResponse] = await Promise.all([
          axios.get('/api/admin/generations', {
            params: { page: 1, limit: 10, sortBy: 'created_at', sortOrder: 'DESC' },
          }),
          axios.get('/api/admin/services/status'),
        ]);

        const genPayload = generationsResponse.data;
        const gens: AdminGeneration[] = Array.isArray(genPayload)
          ? genPayload
          : Array.isArray(genPayload?.generations)
            ? genPayload.generations
            : [];
        setAdminGenerations(gens.slice(0, 10));


        const servicesPayload = servicesResponse.data;
        const summary: ServiceSummary | null = servicesPayload?.success ? servicesPayload?.data?.summary ?? null : null;
        setServicesSummary(summary);

        setGenerations([]);
        return;
      }

      const [generationsResponse, pricingResponse] = await Promise.all([
        axios.get('/api/users/generations'),
        // Цены и бесплатные генерации — подсказка; их сбой не ломает дашборд.
        axios.get('/api/templates/pricing').catch(() => null),
      ]);
      setGenerations(generationsResponse.data || []);
      const pricingData = pricingResponse?.data;
      setPricing(Number.isFinite(pricingData?.single_page) && Number.isFinite(pricingData?.multi_page) ? pricingData : null);
      setAdminGenerations([]);

      setServicesSummary(null);
    } catch (e) {
      console.error('Error fetching dashboard data:', e);
      setError('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownload = async (generationId: number) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Необходимо войти в систему');
        return;
      }

      const response = await axios.get(`/api/users/generations/${generationId}/download`, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `generation-${generationId}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Error downloading file:', e);
      alert('Ошибка при скачивании файла');
    }
  };

  if (loading) {
    return (
      <section className="u2-card is-gradient">
        <div className="u2-cardInner">
          <div className="u2-strong">Загрузка…</div>
          <div className="u2-muted" style={{ fontSize: 12, marginTop: 6 }}>
            Получаем данные дашборда.
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="u2-card is-gradient">
        <div className="u2-cardInner">
          <div className="u2-cardHeader">
            <div>
              <h1 className="u2-title" style={{ fontSize: 22 }}>
                Ошибка
              </h1>
              <p className="u2-subtitle">{error}</p>
            </div>
            <span className="u2-pill is-bad">ошибка</span>
          </div>
          <div className="u2-divider" />
          <button onClick={fetchDashboardData} className="u2-btn is-primary" type="button">
            Попробовать снова
          </button>
        </div>
      </section>
    );
  }

  if (!user) {
    return (
      <section className="u2-card is-gradient">
        <div className="u2-cardInner">
          <div className="u2-cardHeader">
            <div>
              <h1 className="u2-title" style={{ fontSize: 22 }}>
                Не авторизован
              </h1>
              <p className="u2-subtitle">Для доступа к панели управления необходимо войти в систему.</p>
            </div>
          </div>
          <div className="u2-divider" />
          <a href={portalLoginUrl()} className="u2-btn is-primary">
            Войти
          </a>
        </div>
      </section>
    );
  }

  if (isAdmin) {
    return (
      <div className="u2-grid" style={{ gap: 18 }}>
        <CreateSiteActions />
        <section className="u2-card is-gradient">
          <div className="u2-cardInner">
            <div className="u2-cardHeader">
              <div>
                <h1 className="u2-title" style={{ fontSize: 22 }}>
                  Дашборд администратора
                </h1>
                <p className="u2-subtitle">Быстрый обзор системы: генерации, пользователи, сервисы.</p>
              </div>
              <span className="u2-badge is-accent">admin</span>
            </div>

            <div className="u2-grid cols-4">
              <div className="u2-card" style={{ boxShadow: 'none', background: 'var(--u2-surface-3)' }}>
                <div className="u2-cardInner">
                  <div className="u2-kpiLabel">Генераций в сводке</div>
                  <div className="u2-kpiValue">{adminGenerations.length || '—'}</div>
                  <div className="u2-muted" style={{ fontSize: 12 }}>
                    в списке (кратко)
                  </div>
                </div>
              </div>
              <div className="u2-card" style={{ boxShadow: 'none', background: 'var(--u2-surface-3)' }}>
                <div className="u2-cardInner">
                  <div className="u2-kpiLabel">Генераций</div>
                  <div className="u2-kpiValue">{adminGenerations.length || '—'}</div>
                  <div className="u2-muted" style={{ fontSize: 12 }}>
                    последние 10
                  </div>
                </div>
              </div>
              <div className="u2-card" style={{ boxShadow: 'none', background: 'var(--u2-surface-3)' }}>
                <div className="u2-cardInner">
                  <div className="u2-kpiLabel">Сервисы</div>
                  <div className="u2-kpiValue">{servicesSummary ? servicesSummary.totalServices : '—'}</div>
                  <div className="u2-muted" style={{ fontSize: 12 }}>
                    в мониторинге
                  </div>
                </div>
              </div>
              <div className="u2-card" style={{ boxShadow: 'none', background: 'var(--u2-surface-3)' }}>
                <div className="u2-cardInner">
                  <div className="u2-kpiLabel">Финансы</div>
                  <div className="u2-kpiValue">—</div>
                  <div className="u2-muted" style={{ fontSize: 12 }}>
                    будет в следующей версии
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="u2-card is-gradient">
          <div className="u2-cardInner">
            <div className="u2-cardHeader">
              <div>
                <h2 className="u2-title">Управление сервисом</h2>
                <p className="u2-subtitle">Настройки, шаблоны и инструменты проверки собраны в админ-панели.</p>
              </div>
            </div>

            <div className="u2-row">
              <Link className="u2-btn is-secondary" to="/admin">
                Панель администратора
              </Link>
            </div>
          </div>
        </section>

        <section className="u2-card is-gradient">
          <div className="u2-cardInner">
            <div className="u2-cardHeader">
              <div>
                <h2 className="u2-title">Провайдеры генерации</h2>
                <p className="u2-subtitle">Данные из мониторинга сервисов.</p>
              </div>
              <Link className="u2-btn is-secondary" to="/admin/services">
                Открыть мониторинг
              </Link>
            </div>

            {servicesSummary ? (
              <div className="u2-grid cols-4">
                <div className="u2-card" style={{ boxShadow: 'none', background: 'var(--u2-surface-3)' }}>
                  <div className="u2-cardInner">
                    <div className="u2-kpiLabel">Всего</div>
                    <div className="u2-kpiValue">{servicesSummary.totalServices}</div>
                  </div>
                </div>
                <div className="u2-card" style={{ boxShadow: 'none', background: 'var(--u2-surface-3)' }}>
                  <div className="u2-cardInner">
                    <div className="u2-kpiLabel">Доступно</div>
                    <div className="u2-kpiValue">{servicesSummary.availableStock + servicesSummary.availableFallback}</div>
                  </div>
                </div>
                <div className="u2-card" style={{ boxShadow: 'none', background: 'var(--u2-surface-3)' }}>
                  <div className="u2-cardInner">
                    <div className="u2-kpiLabel">Не проверено</div>
                    <div className="u2-kpiValue">{servicesSummary.unchecked}</div>
                  </div>
                </div>
                <div className="u2-card" style={{ boxShadow: 'none', background: 'var(--u2-surface-3)' }}>
                  <div className="u2-cardInner">
                    <div className="u2-kpiLabel">Последняя проверка</div>
                    <div className="u2-kpiValue" style={{ fontSize: 18 }}>
                      {servicesSummary.lastFullCheck ? formatDateTime(servicesSummary.lastFullCheck) : '—'}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="u2-muted" style={{ fontSize: 12 }}>
                Нет данных (проверьте доступ к /api/admin/services/status).
              </div>
            )}
          </div>
        </section>

        <section className="u2-card is-gradient">
          <div className="u2-cardInner">
            <div className="u2-cardHeader">
              <div>
                <h2 className="u2-title">Последние генерации</h2>
                <p className="u2-subtitle">10 последних генераций (админ‑вид).</p>
              </div>
              <Link className="u2-btn is-secondary" to="/admin-generations">
                Все генерации
              </Link>
            </div>

            <div className="u2-tableWrap" aria-label="Последние генерации (админ)">
              <table className="u2-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Пользователь</th>
                    <th>Шаблон</th>
                    <th>Статус</th>
                    <th>Дата</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {adminGenerations.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="u2-muted" style={{ padding: 14 }}>
                        Нет данных
                      </td>
                    </tr>
                  ) : (
                    adminGenerations.map((g) => (
                      <tr key={g.id}>
                        <td title={g.generation_id}>{g.generation_id}</td>
                        <td>{g.user_email}</td>
                        <td>{g.template_type}</td>
                        <td>
                          <span className={`u2-pill ${getStatusPillClass(g.status)}`}>{getStatusText(g.status)}</span>
                        </td>
                        <td>{formatDateTime(g.created_at)}</td>
                        <td>
                          {g.status === 'completed' && g.archive_path ? (
                            <GenerationPreviewButton generationId={g.generation_id} />
                          ) : <span className="u2-muted">—</span>}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="u2-card"><div className="u2-cardInner"><h2 className="u2-title">Аккаунты и баланс — в портале</h2><p className="u2-muted">Общие настройки пользователей доступны в центральной админке.</p><Link className="u2-btn is-secondary" to="/admin?tab=users">Открыть общую админку</Link></div></section>
      </div>
    );
  }

  return (
    <div className="u2-grid" style={{ gap: 18 }}>
      <CreateSiteActions />
      <section className="u2-card is-gradient">
        <div className="u2-cardInner">
          <div className="u2-cardHeader">
            <div>
              <h1 className="u2-title" style={{ fontSize: 22 }}>
                Обзор
              </h1>
              <p className="u2-subtitle">Создайте сайт по шаблону, затем откройте результат или скачайте архив.</p>
            </div>
            <Link to="/generator-v2" className="u2-btn is-primary">Создать сайт</Link>
          </div>

          <div className="u2-grid cols-4">
            <div className="u2-card" style={{ boxShadow: 'none', background: 'var(--u2-surface-3)' }}>
              <div className="u2-cardInner">
                <div className="u2-kpiLabel">Баланс, USD</div>
                <div className="u2-kpiValue">{formatBalanceUSD(user.balance)}</div>
              </div>
            </div>
            <div className="u2-card" style={{ boxShadow: 'none', background: 'var(--u2-surface-3)' }}>
              <div className="u2-cardInner">
                <div className="u2-kpiLabel">Бесплатные генерации</div>
                <div className="u2-kpiValue">{Math.max(0, Number(pricing?.free?.remaining) || 0) + (Number(user.generation_limit) || 0)}</div>
                <p className="u2-muted">
                  Используются в первую очередь. Затем — оплата с баланса
                  {pricing ? `: ${formatBalanceUSD(pricing.single_page)} за лендинг, ${formatBalanceUSD(pricing.multi_page)} за многостраничный сайт.` : '.'}
                </p>
              </div>
            </div>
            <div className="u2-card" style={{ boxShadow: 'none', background: 'var(--u2-surface-3)' }}>
              <div className="u2-cardInner">
                <div className="u2-kpiLabel">Генераций</div>
                <div className="u2-kpiValue">{generations.length}</div>
              </div>
            </div>
            <div className="u2-card" style={{ boxShadow: 'none', background: 'var(--u2-surface-3)' }}>
              <div className="u2-cardInner">
                <div className="u2-kpiLabel">Статус</div>
                <div className="u2-row" style={{ justifyContent: 'flex-start', gap: 10 }}>
                  <span className="u2-pill is-ok">готово: {completedCount}</span>
                  <span className="u2-pill is-warn">в очереди: {pendingCount}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="u2-card is-gradient">
        <div className="u2-cardInner">
          <div className="u2-cardHeader">
            <div>
              <h2 className="u2-title">Быстрые действия</h2>
              <p className="u2-subtitle">Частые сценарии работы.</p>
            </div>
          </div>

          <div className="u2-grid cols-4">
            <Link to="/generator-v2" className="u2-btn is-primary">
              Создать сайт
            </Link>
            <Link to="/history" className="u2-btn is-secondary">
              История
            </Link>
            <Link to="/profile" className="u2-btn is-secondary">
              Профиль
            </Link>

          </div>
        </div>
      </section>

      <section className="u2-card is-gradient">
        <div className="u2-cardInner">
          <div className="u2-cardHeader">
            <div>
              <h2 className="u2-title">Последние генерации</h2>
              <p className="u2-subtitle">Ваши последние работы.</p>
            </div>
            <Link to="/history" className="u2-btn is-secondary">
              Все генерации
            </Link>
          </div>

          <div className="u2-tableWrap" aria-label="Последние генерации (пользователь)">
            <table className="u2-table u2-siteTable">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Тип</th>
                  <th>Юрисдикция</th>
                  <th>Сайт</th>
                  <th>Язык</th>
                  <th>Статус</th>
                  <th>Дата</th>
                  <th style={{ textAlign: 'right' }}></th>
                </tr>
              </thead>
              <tbody>
                {generations.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="u2-muted" style={{ padding: 14 }}>
                      Нет генераций. Откройте генератор и создайте первый сайт.
                    </td>
                  </tr>
                ) : (
                  generations.slice(0, 10).map((g) => (
                    <tr key={g.id}>
                      <td data-label="Номер" title={g.generation_id}>{g.generation_id}</td>
                      <td data-label="Шаблон">{formatGenerationTemplate(g.template_type)}</td>
                      <td data-label="Юрисдикция">{g.jurisdiction}</td>
                      <td data-label="Формат">{formatGenerationSiteType(g.site_type)}</td>
                      <td data-label="Язык">{g.language}</td>
                      <td data-label="Статус">
                        <span className={`u2-pill ${getStatusPillClass(g.status)}`}>{getStatusText(g.status)}</span>
                      </td>
                      <td data-label="Создан">{formatDateTime(g.created_at)}</td>
                      <td data-label="Действия" style={{ textAlign: 'right' }}>
                        {g.status === 'completed' && g.archive_path ? (
                          <div className="u2-toolbarGroup" style={{ justifyContent: 'flex-end' }}>
                          <GenerationPreviewButton generationId={g.generation_id} />
                          <button className="u2-btn is-secondary" type="button" onClick={() => handleDownload(g.id)}>
                            Скачать
                          </button>
                          </div>
                        ) : (
                          <span className="u2-muted" style={{ fontSize: 12 }}>
                            —
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DashboardV5;
